import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import type { ReviewCompanionshipType } from 'api/review'
import type { ReviewPanelSortType } from 'constants/review'

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const RadioGroup = styled.div`
  padding: ${rem(16)};
`

interface Props {
  searchInput: string
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSearchSubmit: () => void
  sort: ReviewPanelSortType
  defaultSort: ReviewPanelSortType
  onSortChange: (value: ReviewPanelSortType) => void
  ratingFilter: string
  onRatingChange: (value: string) => void
  travellerFilter: ReviewCompanionshipType | ''
  onTravellerChange: (value: ReviewCompanionshipType | '') => void
}

function ReviewFilterBar({
  searchInput,
  onSearchChange,
  onSearchSubmit,
  sort,
  defaultSort,
  onSortChange,
  ratingFilter,
  onRatingChange,
  travellerFilter,
  onTravellerChange,
}: Props) {
  const intl = useIntl()

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit()
    }
  }, [onSearchSubmit])

  // Translated option arrays
  const sortOptions = useMemo(() => [
    {
      label: intl.formatMessage({
        id: '1wUerboKIT5phrK-PBoj',
        defaultMessage: 'Most recent',
        description: 'Sort option for most recent reviews in the guest reviews section',
      }),
      value: 'newest' as const,
    },
    {
      label: intl.formatMessage({
        id: 'mb1YNAvVHCc-fL5KaYFp',
        defaultMessage: 'Highest rated',
        description: 'Sort option for highest rated reviews in the guest reviews section',
      }),
      value: 'best_rating' as const,
    },
    {
      label: intl.formatMessage({
        id: 'RUEADNxFIqB_-P8TuY_O',
        defaultMessage: 'Lowest rated',
        description: 'Sort option for lowest rated reviews in the guest reviews section',
      }),
      value: 'worst_rating' as const,
    },
    {
      label: intl.formatMessage({
        id: '6Tx0S8dZOSMAquye28oG',
        defaultMessage: 'Recommended',
        description: 'Sort option for recommended reviews in the guest reviews section',
      }),
      value: 'recommend' as const,
    },
  ], [intl])

  const ratingOptions = useMemo(() => [
    {
      label: intl.formatMessage({
        id: 'FABS5Ng9ZGA8Z1GG52tQ',
        defaultMessage: 'All',
        description: 'Rating filter option to show all reviews in the filters panel',
      }),
      value: '',
    },
    {
      label: intl.formatMessage({
        id: 'CkXRSV_lbRZ7fxOh-ld2',
        defaultMessage: 'Exceptional (9-10)',
        description: 'Rating filter option for exceptional reviews in the filters panel',
      }),
      value: 'exceptional',
    },
    {
      label: intl.formatMessage({
        id: 'CmwGLI_CdzQjWaXJkHle',
        defaultMessage: 'Good (7-8)',
        description: 'Rating filter option for good reviews in the search or listing filters panel',
      }),
      value: 'good',
    },
    {
      label: intl.formatMessage({
        id: 'u-7vBDPSMnOUgdGHAoNY',
        defaultMessage: 'Okay (5-6)',
        description: 'Rating filter option for okay reviews in the search or listing filters panel',
      }),
      value: 'okay',
    },
    {
      label: intl.formatMessage({
        id: '7xO1ZrGYChDkUdJNDV1k',
        defaultMessage: 'Poor (3-4)',
        description: 'Rating filter option for poor reviews in the search or listing filters panel',
      }),
      value: 'poor',
    },
    {
      label: intl.formatMessage({
        id: 'TLsW_5dhIJMdzY8341mY',
        defaultMessage: 'Terrible (1-2)',
        description: 'Rating filter option for terrible reviews in the filters panel',
      }),
      value: 'very-poor',
    },
  ], [intl])

  const travellerOptions = useMemo(() => [
    {
      label: intl.formatMessage({
        id: 'HxzO749jJJwiyuReBQFB',
        defaultMessage: 'All',
        description: 'Traveller filter option to show all reviews in the filters panel',
      }),
      value: '' as const,
    },
    {
      label: intl.formatMessage({
        id: 'jfnoOfSrXqikUDS6Cayw',
        defaultMessage: 'Partner',
        description: 'Traveller filter option for partner travel in the filters panel',
      }),
      value: 'partner' as const,
    },
    {
      label: intl.formatMessage({
        id: '0bnOtj8XqEJWf-CpZUpM',
        defaultMessage: 'Family',
        description: 'Traveller filter option for family travel in the filters panel',
      }),
      value: 'family' as const,
    },
    {
      label: intl.formatMessage({
        id: 'cOA3rzpy5zOY81l22l97',
        defaultMessage: 'Friends',
        description: 'Traveller filter option for friends travel in the filters panel',
      }),
      value: 'friends' as const,
    },
    {
      label: intl.formatMessage({
        id: 'uBe5TuerXNEd51kldKhB',
        defaultMessage: 'Just myself',
        description: 'Traveller filter option for solo travel in the search or listing filters panel',
      }),
      value: 'alone' as const,
    },
  ], [intl])

  // Dropdown state (internal)
  const [openDropdown, setOpenDropdown] = useState<'sort' | 'rating' | 'traveller' | null>(null)
  const isSortOpen = openDropdown === 'sort'
  const isRatingOpen = openDropdown === 'rating'
  const isTravellerOpen = openDropdown === 'traveller'
  const openSort = useCallback(() => setOpenDropdown('sort'), [])
  const openRating = useCallback(() => setOpenDropdown('rating'), [])
  const openTraveller = useCallback(() => setOpenDropdown('traveller'), [])
  const closeDropdown = useCallback(() => setOpenDropdown(null), [])

  // Refs for dropdown anchors
  const sortRef = useRef<HTMLButtonElement>(null)
  const ratingRef = useRef<HTMLButtonElement>(null)
  const travellerRef = useRef<HTMLButtonElement>(null)

  // Wrap parent handlers to close dropdown after selection
  const handleSortSelect = useCallback((value: ReviewPanelSortType) => {
    onSortChange(value)
    closeDropdown()
  }, [onSortChange, closeDropdown])

  const handleRatingSelect = useCallback((value: string) => {
    onRatingChange(value)
    closeDropdown()
  }, [onRatingChange, closeDropdown])

  const handleTravellerSelect = useCallback((value: ReviewCompanionshipType | '') => {
    onTravellerChange(value)
    closeDropdown()
  }, [onTravellerChange, closeDropdown])

  // Derive chip labels from translated arrays
  const defaultSortLabel = sortOptions.find(o => o.value === defaultSort)?.label ?? sortOptions[0]?.label
  const currentSortLabel = sortOptions.find(o => o.value === sort)?.label ?? defaultSortLabel
  const currentRatingLabel = ratingOptions.find(o => o.value === ratingFilter)?.label
  const currentTravellerLabel = travellerOptions.find(o => o.value === travellerFilter)?.label

  // Translated chip labels
  const sortChipLabel = intl.formatMessage(
    {
      id: '58z-2fc_hwRD0hLGXqyP',
      defaultMessage: 'Sort: {sortLabel}',
      description: 'Chip label showing the currently selected review sort option',
    },
    { sortLabel: currentSortLabel },
  )

  const ratingsLabel = intl.formatMessage({
    id: 'NSMZzsuxrmTvbM0SKMrZ',
    defaultMessage: 'Ratings',
    description: 'Chip label for ratings filter when no specific rating is selected',
  })
  const ratingsWithFilterLabel = intl.formatMessage(
    {
      id: '2bKOZENJlp0hlryp6qso',
      defaultMessage: 'Ratings: {filterLabel}',
      description: 'Chip label for ratings filter showing the selected rating',
    },
    { filterLabel: currentRatingLabel },
  )
  const ratingsChipLabel = ratingFilter ? ratingsWithFilterLabel : ratingsLabel

  const travellersLabel = intl.formatMessage({
    id: 'WDFev96J3Z0NvouqMgN4',
    defaultMessage: 'Travellers',
    description: 'Chip label for travellers filter when no specific type is selected',
  })
  const travellersWithFilterLabel = intl.formatMessage(
    {
      id: 'A6SkYyV8hd2DT0AbNVPP',
      defaultMessage: 'Travellers: {filterLabel}',
      description: 'Chip label for travellers filter showing the selected type',
    },
    { filterLabel: currentTravellerLabel },
  )
  const travellersChipLabel = travellerFilter ? travellersWithFilterLabel : travellersLabel

  const searchReviewsPlaceholder = intl.formatMessage({
    id: '0OmZ5MAM6nj45vkgJESg',
    defaultMessage: 'Search reviews',
    description: 'Placeholder text for the review search input in the search interface',
  })
  const searchAriaLabel = intl.formatMessage({
    id: '_wNK7-ke5GALXOAF8x75',
    defaultMessage: 'Search',
    description: 'Aria-label for the review search button in the search and discovery interface',
  })

  return (
    <>
      <SearchInputWrapper>
        <TextInputOnly
          placeholder={searchReviewsPlaceholder}
          value={searchInput}
          onChange={onSearchChange}
          onKeyDown={handleSearchKeyDown}
          noValidationSpacing
          endIcon={
            <IconButton
              kind="tertiary"
              variant="default"
              size="small"
              onClick={onSearchSubmit}
              aria-label={searchAriaLabel}
            >
              <LineSearchIcon />
            </IconButton>
          }
        />
      </SearchInputWrapper>
      {/* Always portal: ReviewSectionRoot's container-type is a containing block for position:fixed. */}
      <FilterChips>
        {/* Sort Dropdown */}
        <AssistChip
          ref={sortRef}
          size="medium"
          variant="filled"
          onClick={openSort}
        >
          {sortChipLabel}
        </AssistChip>
        <DropdownList
          size="S"
          open={isSortOpen}
          onClose={closeDropdown}
          anchorRef={sortRef}
          triggerRef={sortRef}
          portaled
        >
          <RadioGroup>
            <Group direction="vertical" gap={16}>
              {sortOptions.map(option => (
                <RadioInput
                  key={option.value}
                  checked={sort === option.value}
                  onChange={() => handleSortSelect(option.value)}
                  name="sort"
                  value={option.value}
                >
                  {option.label}
                </RadioInput>
              ))}
            </Group>
          </RadioGroup>
        </DropdownList>

        {/* Ratings Filter Dropdown */}
        <AssistChip
          ref={ratingRef}
          size="medium"
          variant="filled"
          onClick={openRating}
        >
          {ratingsChipLabel}
        </AssistChip>
        <DropdownList
          size="S"
          open={isRatingOpen}
          onClose={closeDropdown}
          anchorRef={ratingRef}
          triggerRef={ratingRef}
          portaled
        >
          <RadioGroup>
            <Group direction="vertical" gap={16}>
              {ratingOptions.map(option => (
                <RadioInput
                  key={option.value}
                  checked={ratingFilter === option.value}
                  onChange={() => handleRatingSelect(option.value)}
                  name="rating"
                  value={option.value}
                >
                  {option.label}
                </RadioInput>
              ))}
            </Group>
          </RadioGroup>
        </DropdownList>

        {/* Travellers Filter Dropdown */}
        <AssistChip
          ref={travellerRef}
          size="medium"
          variant="filled"
          onClick={openTraveller}
        >
          {travellersChipLabel}
        </AssistChip>
        <DropdownList
          size="S"
          open={isTravellerOpen}
          onClose={closeDropdown}
          anchorRef={travellerRef}
          triggerRef={travellerRef}
          portaled
        >
          <RadioGroup>
            <Group direction="vertical" gap={16}>
              {travellerOptions.map(option => (
                <RadioInput
                  key={option.value}
                  checked={travellerFilter === option.value}
                  onChange={() => handleTravellerSelect(option.value as ReviewCompanionshipType | '')}
                  name="traveller"
                  value={option.value}
                >
                  {option.label}
                </RadioInput>
              ))}
            </Group>
          </RadioGroup>
        </DropdownList>
      </FilterChips>
    </>
  )
}

export default React.memo(ReviewFilterBar)
